import React, { useState, useEffect, useRef } from 'react';
import './OffersGallery.css';
import { useAuth } from '../components/AuthContext';
import useCartContext from '../hooks/useCartContext';
import QuantityModal from './QuantityModal';
import { db } from '../utils/firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Cache para ofertas
const OFFERS_CACHE_KEY = 'mi_ecommerce_offers_cache';
const OFFERS_CACHE_DURATION = 15 * 60 * 1000; // 15 minutos

const getOffersFromCache = () => {
  try {
    const stored = localStorage.getItem(OFFERS_CACHE_KEY);
    if (!stored) return null;
    
    const { data, timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp > OFFERS_CACHE_DURATION) {
      localStorage.removeItem(OFFERS_CACHE_KEY);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error leyendo cache ofertas:', error);
    return null;
  }
};

const saveOffersToCache = (data) => {
  try {
    localStorage.setItem(OFFERS_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error guardando cache ofertas:', error);
  }
};

const OffersGallery = () => {
  const { currentUser } = useAuth();
  const { cart, setCart } = useCartContext();

  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showDiscounted, setShowDiscounted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Todas');

  // Paginación lateral
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;

  // Swipe
  const carouselRef = useRef();
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    if (carouselRef.current) {
      setScrollWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
    }
  }, [filteredOffers]);

  const handleSwipe = (offsetX) => {
    const threshold = 80;
    if (offsetX < -threshold && currentIndex + itemsPerPage < filteredOffers.length) {
      handleNext();
    } else if (offsetX > threshold && currentIndex > 0) {
      handlePrev();
    }
  };

  // === 1️⃣ Cargar ofertas desde Firestore (UNA SOLA VEZ, con caché localStorage) ===
  useEffect(() => {
    const loadOffers = async () => {
      try {
        // Intentar caché primero
        const cached = getOffersFromCache();
        if (cached) {
          setOffers(cached);
          setIsLoading(false);
          return;
        }

        const offersRef = collection(db, 'offers');
        const q = query(offersRef, where('enabled', '==', true));
        
        const snapshot = await getDocs(q);
        const offersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Guardar en caché
        saveOffersToCache(offersList);
        setOffers(offersList);
        setIsLoading(false);
      } catch (err) {
        console.error('Error al obtener las ofertas:', err);
        setError('Error al cargar las ofertas.');
        setIsLoading(false);
      }
    };

    loadOffers();
  }, []);

  // === 2️⃣ Filtros ===
  useEffect(() => {
    let filtered = offers;

    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(
        (offer) =>
          offer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          offer.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (showDiscounted) {
      filtered = filtered.filter((offer) => offer.discount && offer.discount > 0);
    }

    if (selectedCategory !== 'Todas') {
      filtered = filtered.filter((offer) => offer.category === selectedCategory);
    }

    setFilteredOffers(filtered);
    setCurrentIndex(0);
  }, [offers, searchTerm, showDiscounted, selectedCategory]);

  // === 3️⃣ Navegación lateral ===
  const handleNext = () => {
    if (currentIndex + itemsPerPage < filteredOffers.length) {
      setCurrentIndex((prev) => prev + itemsPerPage);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - itemsPerPage);
    }
  };

  const visibleOffers = filteredOffers.slice(currentIndex, currentIndex + itemsPerPage);

  // === 4️⃣ Notificación auto-cierre ===
  useEffect(() => {
    if (notification && notification.autoClose) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // === 5️⃣ Carrito ===
  const handleAddToCart = (offer) => {
    if (!currentUser) {
      setNotification({
        message: 'Por favor, inicia sesión para agregar productos al carrito.',
        type: 'error',
        duration: 2000,
        autoClose: true,
      });
      return;
    }

    if (offer.stock <= 0) {
      setNotification({
        message: `Lo sentimos, ${offer.name} está agotado.`,
        type: 'error',
        duration: 2000,
        autoClose: true,
      });
      return;
    }

    setSelectedProduct(offer);
    setIsModalOpen(true);
  };

  const handleRemoveFromCart = (offer) => {
    const updatedCart = cart.filter((item) => item.id !== offer.id);
    setCart(updatedCart);
    setNotification({
      message: 'Producto eliminado del carrito.',
      type: 'success',
      duration: 2000,
      autoClose: true,
    });
  };

  const handleConfirmQuantity = (quantity) => {
    const existingProduct = cart.find((item) => item.id === selectedProduct.id);
    const productToAdd = {
      ...selectedProduct,
      quantity,
      type: 'combo',
    };

    if (existingProduct) {
      const updatedCart = cart.map((item) =>
        item.id === selectedProduct.id ? { ...item, quantity: item.quantity + quantity } : item
      );
      setCart(updatedCart);
    } else {
      setCart([...cart, productToAdd]);
    }

    setNotification({
      message: 'Producto agregado al carrito.',
      type: 'success',
      duration: 2000,
      autoClose: true,
    });
    setIsModalOpen(false);
  };

  const isInCart = (offerId) => cart.some((item) => item.id === offerId);

  // === Categorías dinámicas ===
  const categories = ['Todas', ...new Set(offers.map((o) => o.category).filter(Boolean))];

  // === Render ===
  if (isLoading) {
    return (
      <div className="offers-gallery">
        <h2 className="offers-h2">Nuestros Combos</h2>
        <p>Cargando ofertas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="offers-gallery">
        <h2 className="offers-h2">Nuestros Combos</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="offers-gallery">
      <h2 className="offers-h2">Nuestros Combos</h2>

      {/* === Filtros === */}
      <div className="offers-filters">
        <input
          type="text"
          placeholder="Buscar combos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="offers-search-input"
        />

        <label className="offers-checkbox">
          <input
            type="checkbox"
            checked={showDiscounted}
            onChange={() => setShowDiscounted((prev) => !prev)}
          />
          Con descuento
        </label>

        {/* 🆕 Dropdown de categorías */}
        <select
          className="offers-sort-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* === Notificación === */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
          <button className="notification-close" onClick={() => setNotification(null)}>×</button>
        </div>
      )}

      {/* === Carrusel horizontal con swipe === */}
      <div className="offers-carousel-container">
        <button
          className="carousel-button left"
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          <ChevronLeft size={30} />
        </button>

        <motion.div
          className="offers-carousel"
          ref={carouselRef}
          drag="x"
          dragConstraints={{ right: 0, left: -scrollWidth }}
          onDragEnd={(event, info) => handleSwipe(info.offset.x)}
        >
          {visibleOffers.map((offer) => (
            <div key={offer.id} className="offer-card">
              <div className="offer-image-container">
                <img src={offer.image} alt={offer.name} className="offer-image" />
                <div className="offer-overlay">
                  <h3 className="offer-title">{offer.name}</h3>
                  <p className="offer-description">{offer.description}</p>

                  {offer.discount ? (
                    <div className="price-container">
                      <p className="old-price">${offer.price}</p>
                      <p className="price discount-price">
                        ${(offer.price * (1 - offer.discount / 100)).toFixed(2)}
                        <span className="discount-label"> -{offer.discount}%</span>
                      </p>
                    </div>
                  ) : (
                    <p className="price">${offer.price}</p>
                  )}

                  {isInCart(offer.id) ? (
                    <button
                      className="remove-from-cart-button"
                      onClick={() => handleRemoveFromCart(offer)}
                    >
                      Quitar del carrito
                    </button>
                  ) : (
                    <button
                      className="add-to-cart-button"
                      onClick={() => handleAddToCart(offer)}
                    >
                      Añadir al carrito
                    </button>
                  )}
                </div>
              </div>
              <span className="badge combo-badge">Combo</span>
            </div>
          ))}
        </motion.div>

        <button
          className="carousel-button right"
          onClick={handleNext}
          disabled={currentIndex + itemsPerPage >= filteredOffers.length}
        >
          <ChevronRight size={30} />
        </button>
      </div>

      <QuantityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmQuantity}
        product={selectedProduct}
      />
    </div>
  );
};

export default OffersGallery;
